import Vue from 'vue'
import VueRouter from 'vue-router'
import { Message } from 'element-ui'

Vue.use(VueRouter)

// 吞掉 push/replace 的良性导航异常（Navigation cancelled/duplicated/aborted/redirected）。
// 这些是"守卫重定向 / 连续两次导航 / 重复点击同一路由"等正常情形，vue-router 3.1+ 会 reject promise；
// 不 catch 就会冒泡成未捕获错误弹窗（例：注册成功 5s 倒计时跳首页 与 "立即前往"按钮抢跑 → Navigation cancelled）。
const _push = VueRouter.prototype.push
VueRouter.prototype.push = function push(loc, onResolve, onReject) {
  if (onResolve || onReject) return _push.call(this, loc, onResolve, onReject)
  return _push.call(this, loc).catch(err => (VueRouter.isNavigationFailure(err) ? err : Promise.reject(err)))
}
const _replace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(loc, onResolve, onReject) {
  if (onResolve || onReject) return _replace.call(this, loc, onResolve, onReject)
  return _replace.call(this, loc).catch(err => (VueRouter.isNavigationFailure(err) ? err : Promise.reject(err)))
}

const routes = [
  {
    path: '/front/home',
    name: 'FrontHome',
    component: () => import('@/views/front/home/index.vue'),
    meta: { title: '首页 - 万博书香' }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/front/auth/login.vue'),
    meta: { title: '用户登录' }
  },
  {
    path: '/bind-opac',
    name: 'BindOPAC',
    component: () => import('@/views/front/auth/bind-opac.vue'),
    meta: { title: '绑定OPAC' }
  },
  {
    path: '/front/register',
    name: 'FrontRegister',
    component: () => import('@/views/front/auth/register.vue'),
    meta: { title: '用户注册' }
  },
  {
    path: '/front/forgot-password',
    name: 'FrontForgotPassword',
    component: () => import('@/views/front/auth/forgot-password.vue'),
    meta: { title: '找回密码' }
  },
  {
    path: '/front/reader/:id',
    name: 'FrontPdfReader',
    component: () => import('@/views/front/reader/index.vue'),
    meta: { title: '在线阅读' }
  },
  {
    // 手机版阅读器（移动端 <web-view> 加载，触控翻页 + 极简界面）
    path: '/front/mreader/:id',
    name: 'FrontMobileReader',
    component: () => import('@/views/front/reader/mobile.vue'),
    meta: { title: '在线阅读' }
  },
  {
    path: '/m/b/:id',
    name: 'MobileBookDeepLink',
    component: () => import('@/views/front/m-redirect.vue'),
    meta: { title: '跳转中…' }
  },
  {
    path: '/front/ranking',
    name: 'FrontRanking',
    component: () => import('@/views/front/ranking/index.vue'),
    meta: { title: '图书排行榜' }
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/login/index.vue'),
    meta: { title: '后台登录' }
  },
  {
    path: '/admin',
    component: () => import('@/layout/AdminLayout.vue'),
    redirect: '/admin/publisher',
    children: [
      {
        path: 'message',
        name: 'AdminMessage',
        component: () => import('@/views/admin/message/index.vue'),
        meta: { title: '消息中心' }
      },
      {
        path: 'reading-channel',
        name: 'AdminReadingChannel',
        component: () => import('@/views/admin/reading-channel/index.vue'),
        meta: { title: '阅读渠道管理' }
      },
      {
        path: 'comment',
        name: 'AdminComment',
        component: () => import('@/views/admin/comment/index.vue'),
        meta: { title: '评论管理' }
      },
      {
        path: 'publisher',
        name: 'AdminPublisher',
        component: () => import('@/views/admin/publisher/index.vue'),
        meta: { title: '出版社管理' }
      },
      {
        path: 'book',
        name: 'AdminBook',
        component: () => import('@/views/admin/book/index.vue'),
        meta: { title: '纸电图书管理' }
      },
      {
        path: 'audiobook',
        name: 'AdminAudiobook',
        component: () => import('@/views/admin/audiobook/index.vue'),
        meta: { title: '有声书管理' }
      },
      {
        path: 'video',
        name: 'AdminVideo',
        component: () => import('@/views/admin/video/index.vue'),
        meta: { title: '视频资源管理' }
      },
      {
        path: 'category',
        name: 'AdminCategory',
        component: () => import('@/views/admin/category/index.vue'),
        meta: { title: '分类管理' }
      },
      {
        path: 'topic',
        name: 'AdminTopic',
        component: () => import('@/views/admin/topic/index.vue'),
        meta: { title: '专题管理' }
      },
      {
        path: 'news',
        name: 'AdminNews',
        component: () => import('@/views/admin/news/index.vue'),
        meta: { title: '资讯管理' }
      },
      {
        path: 'correction',
        name: 'AdminCorrection',
        component: () => import('@/views/admin/correction/index.vue'),
        meta: { title: '纠错管理' }
      },
      {
        path: 'library',
        name: 'AdminLibrary',
        component: () => import('@/views/admin/library/index.vue'),
        meta: { title: '图书馆管理' }
      },
      {
        path: 'holdings-ops',
        name: 'AdminHoldingsOps',
        component: () => import('@/views/admin/holdings-ops/index.vue'),
        meta: { title: '馆藏管理' }
      },
      {
        path: 'order',
        name: 'AdminOrder',
        component: () => import('@/views/admin/order/index.vue'),
        meta: { title: '订单管理' }
      },
      {
        path: 'order/recommend-config',
        name: 'AdminRecommendOrderConfig',
        component: () => import('@/views/admin/order/recommend-config/index.vue'),
        meta: { title: '推荐订单配置' }
      },
      {
        path: 'project',
        name: 'AdminProject',
        component: () => import('@/views/admin/project/index.vue'),
        meta: { title: '项目管控' }
      },
      {
        path: 'ranking',
        name: 'AdminRanking',
        component: () => import('@/views/admin/ranking/index.vue'),
        meta: { title: '榜单运营管理' }
      },
      {
        path: 'new-book-recommend',
        name: 'AdminNewBookRecommend',
        component: () => import('@/views/admin/new-book-recommend/index.vue'),
        meta: { title: '新书速递管理' }
      },
      {
        path: 'order-stats',
        name: 'AdminOrderStats',
        component: () => import('@/views/admin/order-stats/index.vue'),
        meta: { title: '采购统计分析', source: 'admin' }
      },
      // ── 参数管理 ──
      {
        path: 'settings/conversion',
        name: 'AdminConversion',
        component: () => import('@/views/admin/settings/conversion/index.vue'),
        meta: { title: 'MARC与Excel转换规则配置' }
      },
      {
        path: 'settings/quick-nav',
        name: 'AdminQuickNav',
        component: () => import('@/views/admin/settings/quick-nav/index.vue'),
        meta: { title: '快速导航管理' }
      },
      {
        path: 'borrow-setting',
        name: 'AdminBorrowSetting',
        component: () => import('@/views/admin/borrow-setting/index.vue'),
        meta: { title: '电子书借阅设置' }
      },
      // ── 用户与角色管理 ──
      {
        path: 'user/menu',
        name: 'AdminUserMenu',
        component: () => import('@/views/admin/user-menu/index.vue'),
        meta: { title: '菜单管理' }
      },
      {
        path: 'user/role',
        name: 'AdminUserRole',
        component: () => import('@/views/admin/user-role/index.vue'),
        meta: { title: '后台角色管理' }
      },
      {
        path: 'user/account',
        name: 'AdminUserAccount',
        component: () => import('@/views/admin/user/index.vue'),
        meta: { title: '后台用户管理' }
      }
    ]
  },
  {
    path: '/front',
    component: () => import('@/layout/FrontLayout.vue'),
    redirect: '/front/subscribe',
    children: [
      {
        path: 'subscribe',
        name: 'FrontSubscribe',
        component: () => import('@/views/front/subscribe/index.vue'),
        meta: { title: '征订荐购' }
      },
      {
        path: 'co-purchase',
        name: 'FrontCoPurchase',
        component: () => import('@/views/front/co-purchase/index.vue'),
        meta: { title: '纸电同采' }
      },
      {
        path: 'video-resource',
        name: 'FrontVideoResource',
        component: () => import('@/views/front/video-resource/index.vue'),
        meta: { title: '视频资源' }
      },
      {
        path: 'video-resource/detail/:id',
        name: 'FrontVideoDetail',
        component: () => import('@/views/front/video-resource/detail/index.vue'),
        meta: { title: '视频详情' }
      },
      {
        path: 'cd',
        name: 'FrontCd',
        component: () => import('@/views/front/cd/index.vue'),
        meta: { title: '随书光盘' }
      },
      {
        path: 'cd/detail/:id',
        name: 'FrontCdDetail',
        component: () => import('@/views/front/cd/detail/index.vue'),
        meta: { title: '随书光盘详情' }
      },
      {
        path: 'subscribe/detail/:id',
        name: 'FrontSubscribeDetail',
        component: () => import('@/views/front/subscribe/detail/index.vue'),
        meta: { title: '图书详情' }
      },
      {
        path: 'bestseller',
        name: 'FrontBestseller',
        component: () => import('@/views/front/bestseller/index.vue'),
        meta: { title: '畅销书' }
      },
      {
        path: 'topic',
        name: 'FrontTopicList',
        component: () => import('@/views/front/topic/index.vue'),
        meta: { title: '专题推荐' }
      },
      {
        path: 'topic/detail/:id',
        name: 'FrontTopicDetail',
        component: () => import('@/views/front/topic/detail.vue'),
        meta: { title: '专题详情' }
      },
      {
        path: 'audio',
        name: 'FrontAudio',
        component: () => import('@/views/front/audio/index.vue'),
        meta: { title: '有声书' }
      },
      {
        path: 'audio/:id',
        name: 'FrontAudioDetail',
        component: () => import('@/views/front/audio/detail.vue'),
        meta: { title: '有声书详情' }
      },
      {
        path: 'lib-ebooks',
        name: 'FrontLibEbooks',
        component: () => import('@/views/front/lib-ebooks/index.vue'),
        meta: { title: '本馆电子书' }
      },
      {
        path: 'news',
        name: 'FrontNewsList',
        component: () => import('@/views/front/news/index.vue'),
        meta: { title: '资讯' }
      },
      {
        path: 'news/:id',
        name: 'FrontNewsDetail',
        component: () => import('@/views/front/news/detail.vue'),
        meta: { title: '资讯详情' }
      },
      {
        path: 'new-express',
        name: 'FrontNewExpress',
        component: () => import('@/views/front/new-express/index.vue'),
        meta: { title: '新书速递' }
      },
      {
        path: 'publishers',
        name: 'FrontPublisherList',
        component: () => import('@/views/front/publisher-list/index.vue'),
        meta: { title: '入驻出版单位' }
      },
      {
        path: 'cart',
        name: 'FrontCart',
        component: () => import('@/views/front/cart/index.vue'),
        meta: { title: '购物车管理' }
      },
      {
        path: 'lib-order-stats',
        name: 'FrontLibOrderStats',
        component: () => import('@/views/front/order-stats/index.vue'),
        meta: { title: '采购统计分析' }
      },
      {
        path: 'user',
        component: () => import('@/views/front/profile/index.vue'),
        redirect: '/front/user/settings',
        meta: { title: '个人中心' },
        children: [
          // ── 账号管理 ──
          {
            path: 'settings',
            name: 'ProfileSettings',
            component: () => import('@/views/front/profile/settings/index.vue'),
            meta: { title: '用户设置' }
          },
          {
            path: 'org-auth',
            name: 'ProfileOrgAuth',
            component: () => import('@/views/front/profile/org-auth/index.vue'),
            meta: { title: '机构认证' }
          },
          // ── 个性服务 ──
          {
            path: 'personal-read',
            name: 'ProfilePersonalRead',
            component: () => import('@/views/front/profile/personal-read/index.vue'),
            meta: { title: '个性荐读' }
          },
          {
            path: 'preferences',
            name: 'ProfilePreferences',
            component: () => import('@/views/front/profile/preferences/index.vue'),
            meta: { title: '个人偏好' }
          },
          {
            path: 'portrait',
            name: 'ProfilePortrait',
            component: () => import('@/views/front/profile/portrait/index.vue'),
            meta: { title: '用户画像' }
          },
          {
            path: 'help-assistant',
            name: 'ProfileHelpAssistant',
            component: () => import('@/views/front/profile/help-assistant/index.vue'),
            meta: { title: '贴心小助手' }
          },
          {
            path: 'help-manage',
            name: 'ProfileHelpManage',
            component: () => import('@/views/front/profile/help-manage/index.vue'),
            meta: { title: '帮助文档维护' }
          },
          // ── 我的记录 ──
          {
            path: 'my-recommend',
            name: 'ProfileMyRecommend',
            component: () => import('@/views/front/profile/my-recommend/index.vue'),
            meta: { title: '我的荐购' }
          },
          {
            path: 'my-favorites',
            name: 'ProfileMyFavorites',
            component: () => import('@/views/front/profile/my-favorites/index.vue'),
            meta: { title: '我的收藏' }
          },
          {
            path: 'my-errata',
            name: 'ProfileMyErrata',
            component: () => import('@/views/front/profile/my-errata/index.vue'),
            meta: { title: '我的纠错' }
          },
          {
            path: 'my-reading',
            name: 'ProfileMyReading',
            component: () => import('@/views/front/profile/my-reading/index.vue'),
            meta: { title: '我的阅读' }
          },
          {
            path: 'my-reviews',
            name: 'ProfileMyReviews',
            component: () => import('@/views/front/profile/my-reviews/index.vue'),
            meta: { title: '我的评价' }
          },
          {
            path: 'my-messages',
            name: 'ProfileMyMessages',
            component: () => import('@/views/front/profile/my-messages/index.vue'),
            meta: { title: '我的消息' }
          },
          {
            path: 'my-bookmarks',
            name: 'ProfileMyBookmarks',
            component: () => import('@/views/front/profile/my-bookmarks/index.vue'),
            meta: { title: '我的书签' }
          },
          {
            path: 'my-borrowing',
            name: 'ProfileMyBorrowing',
            component: () => import('@/views/front/profile/my-borrowing/index.vue'),
            meta: { title: '我的借阅' }
          },
          // ── 采购馆员专属 ──
          {
            path: 'recommend-manage',
            name: 'ProfileRecommendManage',
            component: () => import('@/views/front/profile/recommend-manage/index.vue'),
            meta: { title: '荐购管理' }
          },
          {
            path: 'cert-review',
            name: 'ProfileCertReview',
            component: () => import('@/views/front/profile/cert-review/index.vue'),
            meta: { title: '认证审核' }
          },
          {
            path: 'lib-order',
            name: 'ProfileLibOrder',
            component: () => import('@/views/front/profile/lib-order/index.vue'),
            meta: { title: '订单管理' }
          },
          {
            path: 'collection-manage',
            name: 'ProfileCollectionManage',
            component: () => import('@/views/front/profile/collection-manage/index.vue'),
            meta: { title: '馆藏管理' }
          },
          {
            path: 'publisher-compare',
            name: 'ProfilePublisherCompare',
            component: () => import('@/views/front/profile/publisher-compare/index.vue'),
            meta: { title: '出版社数据对比' }
          },
          {
            path: 'lib-compare',
            name: 'ProfileLibCompare',
            component: () => import('@/views/admin/library-compare/index.vue'),
            meta: { title: '同类图书馆数据对比' }
          },
          {
            path: 'news-manage',
            name: 'ProfileNewsManage',
            component: () => import('@/views/front/profile/news-manage/index.vue'),
            meta: { title: '资讯管理' }
          },
          {
            path: 'department-dict',
            name: 'ProfileDepartmentDict',
            component: () => import('@/views/front/profile/department-dict/index.vue'),
            meta: { title: '院系字典' }
          },
          {
            path: 'holdings-dedup',
            name: 'ProfileHoldingsDedup',
            component: () => import('@/views/front/profile/holdings-dedup/index.vue'),
            meta: { title: '馆藏查重' }
          },
          // ── 馆方自治（图书馆超管 / 被授权馆员） ──
          {
            path: 'lib-role',
            name: 'ProfileLibRole',
            component: () => import('@/views/front/profile/lib-role/index.vue'),
            meta: { title: '角色管理' }
          },
          {
            path: 'lib-user',
            name: 'ProfileLibUser',
            component: () => import('@/views/front/profile/lib-user/index.vue'),
            meta: { title: '用户管理' }
          },
          {
            path: 'auto-booklist',
            name: 'ProfileAutoBooklist',
            component: () => import('@/views/front/profile/auto-booklist/index.vue'),
            meta: { title: '自动荐购书单' }
          }
        ]
      }
    ]
  },
  {
    path: '/',
    redirect: '/front/home'
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

// 前后台 token 完全隔离（与 store/modules/user.js 中的 key 保持一致）
const FRONT_TOKEN_KEYS = ['wb_front_token', 'wb_session_token'] // 第二个为旧版本兼容
const ADMIN_TOKEN_KEY = 'wb_admin_token'

function hasAnyKey(keys) {
  return keys.some(k => localStorage.getItem(k) || sessionStorage.getItem(k))
}
function hasFrontToken() { return hasAnyKey(FRONT_TOKEN_KEYS) }
function hasAdminToken() { return hasAnyKey([ADMIN_TOKEN_KEY]) }
// 当前后台账号的权限码集合（登录时写入，key 与 store 一致）
const ADMIN_PERMS_KEY = 'wb_admin_perms'
function adminPerms() {
  try { return JSON.parse(sessionStorage.getItem(ADMIN_PERMS_KEY) || localStorage.getItem(ADMIN_PERMS_KEY) || '[]') } catch (e) { return [] }
}
// 后台「路由前缀 → 所需权限码（任一即可）」映射，与 AdminLayout.vue 菜单 $auth 守卫保持一致。
// 更具体的路径放前面（/admin/order/recommend-config 在 /admin/order 之前），用 find 取首个匹配。
const ADMIN_ROUTE_PERMS = [
  ['/admin/message', ['ORDER.LIST', 'BOOK.LIST']],
  ['/admin/comment', ['COMMENT.MANAGE']],
  ['/admin/publisher', ['PUBLISHER.LIST']],
  ['/admin/book', ['BOOK.LIST']],
  ['/admin/audiobook', ['AUDIOBOOK.LIST']],
  ['/admin/video', ['VIDEO.LIST']],
  ['/admin/category', ['CATEGORY.LIST']],
  ['/admin/topic', ['TOPIC.LIST']],
  ['/admin/news', ['NEWS.LIST']],
  ['/admin/correction', ['CORRECTION.LIST']],
  ['/admin/library', ['LIBRARY.LIST']],
  ['/admin/holdings-ops', ['HOLDING_OPS.LIST']],
  ['/admin/order/recommend-config', ['PURCHASE_CONFIG.VIEW']],
  ['/admin/order', ['ORDER.LIST']],
  ['/admin/project', ['PROJECT.LIST']],
  ['/admin/ranking', ['RANKING.LIST']],
  ['/admin/new-book-recommend', ['NEW_EXPRESS.LIST']],
  ['/admin/borrow-setting', ['BORROW_SETTING.CONFIG']],
  ['/admin/settings/conversion', ['CONVERSION.MANAGE']],
  ['/admin/settings/quick-nav', ['QUICK_NAV.MANAGE']],
  ['/admin/reading-channel', ['READING_CHANNEL.LIST']],
  ['/admin/user/menu', ['MENU.LIST']],
  ['/admin/user/role', ['ROLE.LIST']],
  ['/admin/user/account', ['USER.LIST']]
]
function adminRouteRequired(path) {
  const hit = ADMIN_ROUTE_PERMS.find(([p]) => path === p || path.startsWith(p + '/'))
  return hit ? hit[1] : null
}
function firstAccessibleAdmin(perms) {
  const hit = ADMIN_ROUTE_PERMS.find(([, codes]) => codes.some(c => perms.includes(c)))
  return hit ? hit[0] : null
}

router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.title) {
    document.title = to.meta.title + ' - 万博图书馆'
  }
  if ((to.path.startsWith('/front/user') || to.path === '/front/cart' || to.path === '/front/lib-ebooks') && !hasFrontToken()) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }
  if (to.path.startsWith('/admin') && to.path !== '/admin/login' && !hasAdminToken()) {
    next({ path: '/admin/login', query: { redirect: to.fullPath } })
    return
  }
  // 后台页面级权限：目标页需要某权限码但当前账号没有 → 跳到该账号「第一个有权限的后台页」并提示，
  // 避免落到无权限页触发 403 报错。用重定向而非 next(false)（后者会抛 Navigation aborted）。
  if (to.path.startsWith('/admin') && to.path !== '/admin/login' && hasAdminToken()) {
    const required = adminRouteRequired(to.path)
    if (required && !required.some(c => adminPerms().includes(c))) {
      const fallback = firstAccessibleAdmin(adminPerms())
      try { Message.warning('无权限访问该页面') } catch (e) {}
      next(fallback && fallback !== to.path ? fallback : { path: '/admin/login' })
      return
    }
  }
  next()
})

export default router
